import type { SearchResult } from '@/components/shared/SearchBox';
import type { Destination } from '@/features/destinations/utils/Destination';
import { getDestinationRegionLabel } from '@/features/destinations/utils/DestinationRegionLabelMapper';
import { formatSearchedDestination } from '@/utils/formatSearchedDestination';
import type { RelevanceSearchResult } from '../types/RelevanceSearchResult';

export type DestinationResolution = {
  selectedDestination: Destination;
  searchedDestination: string;
  userSearch?: string;
};

export const UNDECIDED_SEARCH_KEYWORDS = [
  `don't know`,
  `dont know`,
  'not sure',
  'need help',
  `haven't picked`,
  'havent picked',
] as const;

export const UNDECIDED_OPTION_ID = 'undecided';

export const OTHER_DESTINATION_NAME = 'Other';
export const UNKNOWN_DESTINATION_NAME = 'Unknown';

export const getFallbackDestinationName = (
  useNewDestinations: boolean,
): string =>
  useNewDestinations ? UNKNOWN_DESTINATION_NAME : OTHER_DESTINATION_NAME;

export const isSyntheticNonDestinationResult = (
  result: RelevanceSearchResult,
): boolean => {
  if (result.aceDestination !== UNKNOWN_DESTINATION_NAME) {
    return false;
  }

  const hasGeoType = Array.isArray(result.type) && result.type.length > 0;
  const hasCountry = Boolean(result.country?.trim());

  return !hasGeoType && !hasCountry;
};

export const findDestinationByName = (
  destinations: Destination[],
  name: string,
): Destination | undefined =>
  destinations.find((destination) => destination.name === name);

export const matchesUndecidedKeywords = (search: string): boolean => {
  const lowerCasedSearch = search.toLowerCase();

  return UNDECIDED_SEARCH_KEYWORDS.some((keyword) =>
    lowerCasedSearch.includes(keyword),
  );
};

export const isUndecidedOnlySearch = (
  searchResults: SearchResult[],
  undecidedOptionId = UNDECIDED_OPTION_ID,
): boolean =>
  searchResults.length === 1 && searchResults[0]?.id === undecidedOptionId;

export const buildDestinationFromRelevanceResult = ({
  relevanceResult,
  destinations,
  useNewDestinations,
}: {
  relevanceResult: RelevanceSearchResult;
  destinations: Destination[];
  useNewDestinations: boolean;
}): DestinationResolution | undefined => {
  const selectedDestination = findDestinationByName(
    destinations,
    relevanceResult.aceDestination,
  );

  if (!selectedDestination) {
    return undefined;
  }

  const regionLabel = getDestinationRegionLabel(
    selectedDestination.name,
    useNewDestinations,
  );

  return {
    selectedDestination: {
      ...selectedDestination,
      name: regionLabel,
    },
    searchedDestination: formatSearchedDestination([
      relevanceResult.name,
      relevanceResult.region,
      relevanceResult.subRegion,
      relevanceResult.country,
    ]),
  };
};

export const buildOtherDestinationResolution = ({
  typedSearch,
  destinations,
  useNewDestinations,
  fallbackDestinationName,
}: {
  typedSearch: string;
  destinations: Destination[];
  useNewDestinations: boolean;
  fallbackDestinationName?: string;
}): DestinationResolution | undefined => {
  const fallbackDestination = findDestinationByName(
    destinations,
    fallbackDestinationName ?? getFallbackDestinationName(useNewDestinations),
  );

  if (!fallbackDestination) {
    return undefined;
  }

  return {
    selectedDestination: fallbackDestination,
    searchedDestination: '',
    userSearch: typedSearch,
  };
};

export type ResolveDestinationInput = {
  typedSearch: string;
  selectedResult?: RelevanceSearchResult;
  relevanceResults: RelevanceSearchResult[];
  searchResults: SearchResult[];
  destinations: Destination[];
  useNewDestinations: boolean;
  undecidedOptionId?: string;
};

export const resolveDestinationFromSearchInput = ({
  typedSearch,
  selectedResult,
  relevanceResults,
  searchResults,
  destinations,
  useNewDestinations,
  undecidedOptionId = UNDECIDED_OPTION_ID,
}: ResolveDestinationInput): DestinationResolution | undefined => {
  const trimmedSearch = typedSearch.trim();

  if (selectedResult) {
    return buildDestinationFromRelevanceResult({
      relevanceResult: selectedResult,
      destinations,
      useNewDestinations,
    });
  }

  if (!trimmedSearch) {
    return undefined;
  }

  const firstRelevanceResult = relevanceResults[0];
  const hasDestinationResults =
    searchResults.length > 0 &&
    !isUndecidedOnlySearch(searchResults, undecidedOptionId);

  if (firstRelevanceResult && hasDestinationResults) {
    if (isSyntheticNonDestinationResult(firstRelevanceResult)) {
      const resolution = buildOtherDestinationResolution({
        typedSearch: trimmedSearch,
        destinations,
        useNewDestinations,
        fallbackDestinationName: getFallbackDestinationName(useNewDestinations),
      });

      if (!resolution) return undefined;

      return {
        ...resolution,
        searchedDestination: formatSearchedDestination([
          firstRelevanceResult.name,
          firstRelevanceResult.region,
          firstRelevanceResult.subRegion,
          firstRelevanceResult.country,
        ]),
      };
    }

    const mappedDestination = buildDestinationFromRelevanceResult({
      relevanceResult: firstRelevanceResult,
      destinations,
      useNewDestinations,
    });

    if (mappedDestination) {
      return mappedDestination;
    }
  }

  return buildOtherDestinationResolution({
    typedSearch: trimmedSearch,
    destinations,
    useNewDestinations,
  });
};
